
import { DYNAMIC_COLORS, DYNAMIC_LEVELS } from './src/components/ui/utils/dynamicColors.js';
import { buttonColorClasses } from './src/components/ui/Buttons/colorVariants.js';
import colors from 'tailwindcss/colors';

export const THEME_COLOR = 'purple';
export const THEME_LEVEL = 600;

const COLOR_LITERAL = new RegExp(`['"\`](${DYNAMIC_COLORS.join('|')})-(${DYNAMIC_LEVELS.join('|')})['"\`]`, 'g');

const colorClasses = (color, level) => [...buttonColorClasses(color, level), `dark:bg-${color}-${level}`];

const expandColorLiterals = (content) => {
  const classes = new Set();

  for (const [, color, level] of content.matchAll(COLOR_LITERAL)) {
    colorClasses(color, level).forEach((name) => classes.add(name));
  }

  return classes.size ? `${content}\n${[...classes].join(' ')}` : content;
};

export default {
  content: {
    files: [
      "./index.html",
      "./src/**/*.{js,jsx,ts,tsx}",
      "!./src/components/shared/cosmetics/{frames,auras}/**",
    ],
    transform: {
      js: expandColorLiterals,
      jsx: expandColorLiterals,
    },
  },
  safelist: [
    ...new Set(DYNAMIC_LEVELS.flatMap((level) => colorClasses(THEME_COLOR, level))),
    'text-neutral-400', 'text-neutral-500',
    'text-red-500', 'text-red-900',
    'text-orange-500', 'text-amber-500', 'text-yellow-500',
    'text-green-500', 'text-emerald-500', 'text-sky-500',
    'hover:text-purple-300', 'hover:text-sky-300', 'hover:text-red-400',
    `focus:ring-${THEME_COLOR}-${THEME_LEVEL}`,
    `focus-within:ring-${THEME_COLOR}-${THEME_LEVEL}`,
    `focus-within:border-${THEME_COLOR}-${THEME_LEVEL}`,
    `focus:border-${THEME_COLOR}-${THEME_LEVEL}`,
  ],
  darkMode: 'class',
  theme: {
    screens: {
      '3xs': '320px',
      '2xs': '450px',
      xs: '576px',
      sm: '640px',
      '2sm': '700px',
      md: '768px',
      '2md': '820px',
      lg: '1024px',
      xl: '1280px',
      '2xl': '1536px',
      '3xl': '1920px',
    },
    extend: {
      colors: {
        focus: `var(--focus-ring, ${colors[THEME_COLOR][THEME_LEVEL]})`,
      },
      borderColor: { line: 'rgb(var(--line) / <alpha-value>)' },
      divideColor: { line: 'rgb(var(--line) / <alpha-value>)' },
      ringColor: { line: 'rgb(var(--line) / <alpha-value>)' },
      keyframes: {
        'check-pop': {
          '0%': { opacity: '0', transform: 'scale(0) rotate(-45deg)' },
          '60%': { opacity: '1', transform: 'scale(1.25) rotate(0deg)' },
          '100%': { opacity: '1', transform: 'scale(1) rotate(0deg)' },
        },
        reveal: {
          '0%': { opacity: '0', transform: 'translateY(8px) scale(0.98)' },
          '100%': { opacity: '1', transform: 'translateY(0) scale(1)' },
        },
        'border-trace': {
          '0%': { strokeDashoffset: '100' },
          '100%': { strokeDashoffset: '0' },
        },
        'step-forward': {
          '0%': { opacity: '0', transform: 'translateX(24px)' },
          '100%': { opacity: '1', transform: 'translateX(0)' },
        },
        'step-back': {
          '0%': { opacity: '0', transform: 'translateX(-24px)' },
          '100%': { opacity: '1', transform: 'translateX(0)' },
        },
        'spinner-rotate': {
          '100%': { transform: 'rotate(360deg)' },
        },
        'spinner-dash': {
          '0%': { strokeDasharray: '1 100', strokeDashoffset: '0' },
          '50%': { strokeDasharray: '62 100', strokeDashoffset: '-14' },
          '100%': { strokeDasharray: '62 100', strokeDashoffset: '-99' },
        },
        'refresh-spin': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(-360deg)' },
        },
        'loader-in': {
          '0%': { opacity: '0', transform: 'scale(0.92)' },
          '100%': { opacity: '1', transform: 'scale(1)' },
        },
        'flyout-start': {
          '0%': { opacity: '0', transform: 'translateX(-8px) scale(0.98)' },
          '100%': { opacity: '1', transform: 'translateX(0) scale(1)' },
        },
        'flyout-end': {
          '0%': { opacity: '0', transform: 'translateX(8px) scale(0.98)' },
          '100%': { opacity: '1', transform: 'translateX(0) scale(1)' },
        },
      },
      animation: {
        'check-pop': 'check-pop 250ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
        reveal: 'reveal 280ms cubic-bezier(0.22, 1, 0.36, 1) backwards',
        'border-trace': 'border-trace 550ms cubic-bezier(0.65, 0, 0.35, 1) both',
        'step-forward': 'step-forward 320ms cubic-bezier(0.22, 1, 0.36, 1) backwards',
        'step-back': 'step-back 320ms cubic-bezier(0.22, 1, 0.36, 1) backwards',
        'spinner-rotate': 'spinner-rotate 1.6s linear infinite',
        'spinner-dash': 'spinner-dash 1.4s ease-in-out infinite',
        'refresh-spin': 'refresh-spin 0.9s cubic-bezier(0.65, 0, 0.35, 1) infinite',
        'loader-in': 'loader-in 220ms cubic-bezier(0.22, 1, 0.36, 1) 150ms backwards',
        'flyout-start': 'flyout-start 180ms cubic-bezier(0.22, 1, 0.36, 1) backwards',
        'flyout-end': 'flyout-end 180ms cubic-bezier(0.22, 1, 0.36, 1) backwards',
      },
    },
  },
  plugins: [],
}